<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      summary: '',
      description: '',
      labels: [],      // the array that gets submitted
      newLabel: '',
    };
  },
  validations: {
    summary: {
      required,
      minLength: minLength(3),
    },
    description: {
      required,
      minLength: minLength(3),
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'integrations/getUIFlags',
    }),
  },
  methods: {
    async addJiraTask() {
      if (this.v$.$invalid) {
        return; // Prevent form submission if validation fails
      }

      const payload = {
        summary: this.summary,
        description: this.description,
        labels: this.labels,
      }
      try {
        await this.$store.dispatch('integrations/createIssue', payload);
        this.onClose();
        useAlert(this.$t('CREATE_JIRA_TASK.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('CREATE_JIRA_TASK.API.ERROR_MESSAGE'));
      }
    },
    onClose() {
      this.v$.$reset();
      this.$emit('close');
    },
    resetForm() {
      this.summary = "";
      this.description = '';
      this.labels = []; // Ensure labels is reset to an array

      // Reset validation if using Vuelidate
      this.v$.$reset();
    },
    addLabel(event) {
      // Stop the event from bubbling up to the form
      event.preventDefault();
      event.stopPropagation();

      if (this.newLabel.trim() && !this.labels.includes(this.newLabel.trim())) {
        this.labels.push(this.newLabel.trim());
      }
      this.newLabel = ''; // Clear input after adding the label
    },
    removeLabel(index) {
      this.labels.splice(index, 1);
    },
  },
};
</script>

<template>
  <woot-modal :show.sync="show"
              :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CREATE_JIRA_TASK.NEW_ISSUE')"
        :header-content="$t('CREATE_JIRA_TASK.SELECTOR_SUBTITLE')"
      />

      <form class="flex flex-col w-full" @submit.prevent="addJiraTask">
        <div class="w-full">
          <label :class="{ error: v$.summary.$error }">
            {{ $t('CREATE_JIRA_TASK.FORM.SUMMARY.LABEL') }}
            <input
              v-model.trim="summary"
              type="text"
              :placeholder="$t('CREATE_JIRA_TASK.FORM.SUMMARY.PLACEHOLDER')"
              @input="v$.summary.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.description.$error }">
            {{ $t('CREATE_JIRA_TASK.FORM.DESCRIPTION.LABEL') }}
            <textarea
              v-model.trim="description"
              :placeholder="$t('CREATE_JIRA_TASK.FORM.DESCRIPTION.PLACEHOLDER')"
              @input="v$.description.$touch"
              rows="4"
              class="w-full"
            ></textarea>
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CREATE_JIRA_TASK.FORM.LABELS.LABEL') }}
            <div class="tag-input-container">
              <input
                v-model.trim="newLabel"
                type="text"
                :placeholder="$t('CREATE_JIRA_TASK.FORM.LABELS.PLACEHOLDER')"
                @keydown.enter.prevent="addLabel"
              />
              <div class="tags mt-2">
                <span
                  class="tag"
                  v-for="(label, index) in labels"
                  :key="index"
                >
                  {{ label }}
                  <button type="button" @click="removeLabel(index)">×</button>
                </span>
              </div>
            </div>
          </label>
        </div>
        <div class="w-full mt-4">
          <woot-submit-button
            :disabled="
              v$.summary.$invalid ||
              v$.description.$invalid ||
              uiFlags.isFetching
            "
            :button-text="$t('CREATE_JIRA_TASK.FORM.SUBMIT')"
            :loading="uiFlags.isFetching"
            button-class="large expanded"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
